/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */
/* istanbul ignore file */
import {
  Component,
  OnInit,
  ViewChild,
  Output,
  EventEmitter,
  Input,
  AfterViewInit,
} from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { ConvertBase64URLToImage } from '../convertToBase64Image/ConvertBase64URLToImage';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import { LoaderService } from 'src/app/sharedServices/loader.service';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { IPaginationService } from 'src/app/models/interfaces/IPaginationService';

@Component({
  selector: 'app-data-table-multiple-selection',
  templateUrl: './data-table-multiple-selection.component.html',
  styleUrls: ['./data-table-multiple-selection.component.css'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      ),
    ]),
  ],
})
export class DataTableMultipleSelectionComponent implements OnInit {
  selection: SelectionModel<any>;
  objectKeys = Object.keys;

  @Input() sharedDataService;
  @Input() paginationService: IPaginationService;
  @Input() EndpointUrl: string;
  @Input() hasPagiantor = true;
  @Input() hasPaginationService = false;
  length = 100;
  pageSize = 50;
  dataSource = new MatTableDataSource();
  pageSizeOptions: number[] = [50, 100, 200, 400];
  expandedElement: any | null;
  sortedData: any[];
  showProgressBar = false;
  currentProgressForProgressBar = 0;
  currentPageIndex: number;

  @Input() hasFilter = true;
  @Input() tableHeader: any;
  @Input() tableData: any;
  @Input() downloadOption: boolean;
  @Input() uploadOption = false;
  @Input() hasRowDisabledFunctionality = true;
  @Input() enableDropdown;
  @Input() copyOption = false;
  @Input() copyAllOption = false;
  @Input() showDropdown = true;
  @Input() progress = 0;
  @Output() checkBoxClick = new EventEmitter<SelectionModel<any>>();

  @Output() stageClick = new EventEmitter<boolean>();
  @Output() contextMenuClick = new EventEmitter<{
    mouseEvent: MouseEvent;
    element: any;
  }>();

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  constructor(
    public readonly imageConverter: ConvertBase64URLToImage,
    private loaderService: LoaderService
  ) {}

  ngOnInit(): void {
    this.currentPageIndex = 0;
    this.loaderService.showProgressBar.subscribe((val: boolean) => {
      this.showProgressBar = val;
    });
    this.loaderService.currentProgressForProgressbar.subscribe(
      (val: number) => {
        this.currentProgressForProgressBar = val;
      }
    );
    this.selection = new SelectionModel<any>(true, []);
    if (this.sharedDataService) {
      this.getData();
    } else {
      this.refresh();
    }
  }

  getData() {
    this.sharedDataService.setPageSize(this.pageSize);
    this.tableHeader = this.sharedDataService.getTableHeader();
    this.sharedDataService.currentTableDataList.subscribe((data) => {
      this.resetSelection();
      this.tableData = data;
      this.refresh();
    });
  }

  refresh() {
    this.length = this.sharedDataService.getTotalItems();
    this.tableData = new MatTableDataSource(this.tableData);
    this.tableData.paginator = this.paginator;
    this.tableData.sort = this.sort;
    if (this.hasPaginationService) {
      this.recalculatePagination();
    }
  }

  applyFilter(filterValue: string) {
    this.tableData.filter = filterValue.trim().toLowerCase();
    if (this.tableData.paginator) {
      this.tableData.paginator.firstPage();
    }
  }

  //  Whether the number of selected elements matches the total number of rows.
  isAllSelected() {
    return this.tableData.filteredData !== undefined
      ? this.selection.selected.length === this.tableData.filteredData.length
      : this.selection.selected.length === this.tableData.length;
  }

  isAllDeselected() {
    return this.tableData.filteredData !== undefined
      ? this.selection.selected.length === 0
      : this.selection.selected.length === 0;
  }
  selectAllRows() {
    this.tableData.filteredData !== undefined
      ? this.tableData.filteredData.forEach((row) => this.selection.select(row))
      : this.tableData.forEach((row) => this.selection.select(row));
  }

  selectRowsFromPage(row: any) {
    this.selection.select(row);
  }
  deselectAllRows() {
    this.tableData.filteredData !== undefined
      ? this.tableData.filteredData.forEach((row) =>
          this.selection.deselect(row)
        )
      : this.tableData.forEach((row) => this.selection.deselect(row));
  }

  // Selects all rows if they are not all selected; otherwise clear selection.
  toggleAll() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.isAllDeselected()
      ? this.selectAllRows()
      : this.selection.clear();

    this.checkBoxClick.emit(this.selection);
  }

  onCheckboxAction(element: MatCheckboxChange, row: any) {
    if (element.checked) {
      this.selection.select(row);
    } else {
      this.selection.deselect(row);
    }
    this.checkBoxClick.emit(this.selection);
  }

  async onPaginate(pageEvent: PageEvent) {
    if (this.hasPaginationService) {
      this.sharedDataService.setPageSize(pageEvent.pageSize);
      if (this.tableData.data.length !== pageEvent.length) {
        this.runPaginationService(pageEvent);
      }
    }
  }

  runPaginationService(pageEvent: PageEvent) {
    this.loaderService.display(true);
    this.paginationService
      .gePaginatedData(pageEvent.pageIndex + 1, this.pageSize)
      .subscribe((records) => {
        if (records !== null && records.buildableContentProducts !== null) {
          const existingIds = new Set(
            this.tableData.data.map((item) => item.fittingEntryID)
          );
          const newData = records.buildableContentProducts.filter(
            (item) => !existingIds.has(item.fittingEntryID)
          );
          this.tableData.data = [...this.tableData.data, ...newData];
          this.currentPageIndex = pageEvent.pageIndex;
          this.pageSize = pageEvent.pageSize;
          this.loaderService.display(false);
        } else {
          this.loaderService.display(false);
        }
      });
  }

  recalculatePagination() {
    this.paginator._intl.getRangeLabel = (
      page: number,
      pageSize: number,
      length: number
    ) => {
      const start = page * pageSize + 1;
      const end =
        (page + 1) * pageSize > this.length
          ? this.length
          : (page + 1) * pageSize;
      this.paginator.length = this.length;
      return `${start} - ${end} of ${length}`;
    };
  }

  stageContent() {
    this.stageClick.emit();
  }

  onDownload() {
    return this.selection.selected;
  }

  onContextMenu(mouseEvent: MouseEvent, element: any) {
    mouseEvent.preventDefault();

    this.contextMenuClick.emit({
      mouseEvent,
      element: this.selection.selected,
    });
  }
  hasContextMenu(row) {
    return row.isEnable || !this.hasRowDisabledFunctionality;
  }
  resetSelection() {
    this.selection.clear();
    this.currentPageIndex = 0;
  }
  sortData(sort: Sort) {
    let data;
    if (this.tableData.filteredData !== undefined) {
      data = this.tableData.filteredData.slice();
    } else {
      data = this.tableData.slice();
    }
    if (!sort.active || sort.direction === '') {
      this.sortedData = data;
      return;
    }

    this.sortedData = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'Image':
          return compare(a.imageData, b.imageData, isAsc);
        case 'Name':
          return compare(a.name, b.name, isAsc);
        case 'Extended Name':
          return compare(a.extendedName, b.extendedName, isAsc);
        case 'Manufacturer':
          return compare(a.manufacturer, b.manufacturer, isAsc);
        case 'Productline':
          return compare(a.productLine, b.productLine, isAsc);
        case 'Material':
          return compare(a.material, b.material, isAsc);
        case 'Product Type':
          return compare(a.productType, b.productType, isAsc);
        case 'Relative File Location':
          return compare(a.relativeFileLocation, b.relativeFileLocation, isAsc);
        case 'Specification':
          return compare(a.specification, b.specification, isAsc);
        case 'FittingEntryID':
          return compare(a.webServiceId, b.webServiceId, isAsc);
        case 'Full Name':
          return compare(a.fullName, b.fullName, isAsc);
        case 'Email':
          return compare(a.email, b.email, isAsc);
        case 'Roles':
          return compare(a, b, isAsc);
        case 'Pages':
          return compare(a.rtPageString, b.rtPageString, isAsc);

        default:
          return 0;
      }
    });
  }
}

function compare(a: number | string, b: number | string, isAsc: boolean) {
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}
